import { useMemo, useEffect } from 'react';
import { shallow } from 'zustand/shallow';

import useDailyResults from './useDailyResults';
import useNonDailyResults from './useNonDailyResults';
import useRentalResults from './useRentalResults';
import useRentalVenueResults from './useRentalVenueResults';
import { DiscoverHelpers, DiscoverActions } from '../../../zustand-stores/DiscoverStore';
import { useDiscoverStore } from '../../../zustand-stores';
import { MainTabEnum, SubTabEnum } from '../../../shared/discover-tabs-enum';

const { updateQueryStatus } = DiscoverActions;
const { formatDateRangeForQuery, formatTimeRangeForQuery } = DiscoverHelpers;

const useDiscoverResults = () => {
  const {
    view,
    subView,
    isDaily,
    cityName,
    isVirtual,
    minimumMen,
    minimumWomen,
    minimumNonBinary,
    programTagIds,
    selectedGenders,
    selectedVenueIds,
    selectedDaysOfWeek,
    selectedSportNames,
    defaultProgramTypes,
    selectedSkillLevels,
    selectedProgramTypes,
    selectedNeighborhoodIds,
    premierProgramsOnly,
    voloPassExclusiveOnly,
  } = useDiscoverStore(
    store => ({
      view: store.view,
      subView: store.subView,
      isDaily: store.isDaily,
      cityName: store.filters.cityName,
      isVirtual: store.filters.isVirtual,
      programTagIds: store.filters.programTagIds,
      minimumMen: store.filters.minimumMen,
      minimumWomen: store.filters.minimumWomen,
      minimumNonBinary: store.filters.minimumNonBinary,
      defaultProgramTypes: store.defaultProgramTypes,
      selectedGenders: store.filters.selectedGenders,
      selectedVenueIds: store.filters.selectedVenueIds,
      selectedSportNames: store.filters.selectedSportNames,
      selectedDaysOfWeek: store.filters.selectedDaysOfWeek,
      selectedSkillLevels: store.filters.selectedSkillLevels,
      selectedProgramTypes: store.filters.selectedProgramTypes,
      selectedNeighborhoodIds: store.filters.selectedNeighborhoodIds,
      voloPassExclusiveOnly: store.filters.voloPassExclusiveOnly,
      premierProgramsOnly: store.filters.premierProgramsOnly,
    }),
    shallow
  );

  const dateRange = formatDateRangeForQuery();
  const timeRange = formatTimeRangeForQuery();

  const sharedFilters = {
    cityName: isVirtual ? 'Virtual' : cityName,
    programTagIds,
    minimumMen,
    minimumWomen,
    minimumNonBinary,
    premierProgramsOnly,
    voloPassExclusiveOnly,
    genders: selectedGenders,
    timeLow: timeRange.start,
    timeHigh: timeRange.end,
    dateLow: dateRange.start?.toISOString() ?? null,
    dateHigh: dateRange.end?.toISOString() ?? null,
    venueIds: selectedVenueIds,
    sportNames: selectedSportNames,
    daysOfWeek: selectedDaysOfWeek,
    skillLevels: selectedSkillLevels,
    neighborhoodIds: selectedNeighborhoodIds,
    isDaily, // if user is looking at daily tab
    programTypes: selectedProgramTypes.length ? selectedProgramTypes : defaultProgramTypes,
  };

  const isRental = view === MainTabEnum.COURT_RENTALS;

  const {
    error: dailyError,
    loading: dailyLoading,
    data: { count: dailyCount, programs: dailyPrograms } = {},
    refresh: { handleRefresh: handleDailyRefresh, refreshing: dailyRefreshing },
    fetchMore: { fetchPageResults: handleDailyFetchPage, fetchMoreLoading: dailyFetchMoreLoading },
  } = useDailyResults(sharedFilters);

  const {
    error: nonDailyError,
    loading: nonDailyLoading,
    data: { programs: nonDailyPrograms, count: nonDailyCount } = {},
    refresh: { handleRefresh: nonDailyHandleRefresh, refreshing: nonDailyRefreshing },
    fetchMore: { fetchPageResults, fetchMoreLoading },
  } = useNonDailyResults(sharedFilters);

  const {
    error: rentalSportError,
    loading: rentalSportLoading,
    data: { rentalsBySport, count: rentalSportCount },
    refresh: { handleRefresh: rentalSportHandleRefresh, refreshing: rentalSportRefreshing },
    fetchMore: {
      fetchPageResults: rentalSportFetchPageResults,
      fetchMoreLoading: rentalSportFetchMoreLoading,
    },
  } = useRentalResults(sharedFilters);

  const {
    error: rentalVenueError,
    loading: rentalVenueLoading,
    data: { rentalsByVenue, count: rentalVenueCount } = {},
    refresh: { handleRefresh: rentalVenueHandleRefresh, refreshing: rentalVenueRefreshing },
    fetchMore: {
      fetchPageResults: rentalVenueFetchPageResults,
      fetchMoreLoading: rentalVenueFetchMoreLoading,
    },
  } = useRentalVenueResults(sharedFilters);

  const filteredResults = useMemo(() => {
    // this allows events to be included
    if (isDaily) return dailyPrograms || [];

    switch (subView) {
      case SubTabEnum.BY_SPORT:
        return rentalsBySport || [];
      case SubTabEnum.BY_VENUE:
        return rentalsByVenue || [];
      default:
        return nonDailyPrograms || [];
    }
  }, [dailyPrograms, nonDailyPrograms, rentalsBySport, rentalsByVenue, subView, isDaily]);

  const programCount = isDaily ? dailyCount : nonDailyCount;
  const rentalCount = subView === SubTabEnum.BY_VENUE ? rentalVenueCount : rentalSportCount;

  const countForResults = isRental ? rentalCount : programCount;

  /* keep loading states and count for UI in DiscoverStore */
  useEffect(() => {
    updateQueryStatus({
      isLoading: nonDailyLoading || dailyLoading || rentalSportLoading || rentalVenueLoading,
    });
  }, [nonDailyLoading, dailyLoading, rentalSportLoading, rentalVenueLoading]);

  useEffect(() => {
    updateQueryStatus({ resultsCount: countForResults });
  }, [countForResults]);

  const programRefreshing = isDaily ? dailyRefreshing : nonDailyRefreshing;
  const programHandleRefresh = isDaily ? handleDailyRefresh : nonDailyHandleRefresh;
  const programFetchPageResults = isDaily ? handleDailyFetchPage : fetchPageResults;
  const programFetchMoreLoading = isDaily ? dailyFetchMoreLoading : fetchMoreLoading;

  const rentalRefreshing =
    subView === SubTabEnum.BY_SPORT ? rentalSportRefreshing : rentalVenueRefreshing;
  const rentalHandleRefresh =
    subView === SubTabEnum.BY_SPORT ? rentalSportHandleRefresh : rentalVenueHandleRefresh;
  const rentalFetchPageResults =
    subView === SubTabEnum.BY_SPORT ? rentalSportFetchPageResults : rentalVenueFetchPageResults;
  const rentalFetchMoreLoading =
    subView === SubTabEnum.BY_SPORT ? rentalSportFetchMoreLoading : rentalVenueFetchMoreLoading;

  return {
    results: filteredResults,
    error: dailyError || nonDailyError || rentalSportError || rentalVenueError,
    loading: dailyLoading || nonDailyLoading || rentalSportLoading || rentalVenueLoading,
    count: countForResults,
    refreshing: isRental ? rentalRefreshing : programRefreshing,
    handleRefresh: isRental ? rentalHandleRefresh : programHandleRefresh,
    fetchPageResults: isRental ? rentalFetchPageResults : programFetchPageResults,
    fetchMoreLoading: isRental ? rentalFetchMoreLoading : programFetchMoreLoading,
  };
};

export default useDiscoverResults;
